import { Link, useParams } from 'react-router-dom';
import { articles, categories } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-24 pb-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100 text-foreground-300">
              <i className="ri-file-unknow-line text-2xl"></i>
            </div>
            <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-2">
              文章未找到
            </h2>
            <p className="text-foreground-500 mb-6">您访问的文章可能已被移除或链接有误。</p>
            <Link
              to="/category/all"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回文章列表
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  const categoryInfo = categories.find((c) => c.slug === article.categorySlug);

  return (
    <>
      <Navbar />

      <main>
        {/* Breadcrumb */}
        <section className="w-full bg-background-100 pt-24 pb-4 md:pt-28">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-400 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-foreground-600 transition-colors whitespace-nowrap">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link
                to={`/category/${article.categorySlug}`}
                className="hover:text-foreground-600 transition-colors whitespace-nowrap"
              >
                {article.category}
              </Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-600 font-medium line-clamp-1">{article.title}</span>
            </nav>
          </div>
        </section>

        {/* Article Header */}
        <section className="w-full bg-background-100 pb-10">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-4 whitespace-nowrap">
              {article.category}
            </span>
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-6 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
              <span>{article.author}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span>{article.date}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span>{article.readTime}</span>
            </div>
          </div>
        </section>

        {/* Article Image */}
        <section className="w-full bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="rounded-xl overflow-hidden -mt-6">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full aspect-video object-cover"
                title={`${article.title} — 牧教师`}
              />
            </div>
          </div>
        </section>

        {/* Article Body */}
        <section className="w-full bg-background-50 py-10 md:py-14">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <article
              className="article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            <div className="mt-10 pt-8 border-t border-background-200">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-foreground-400">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-14 md:py-16 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.slug}`}
                    className="group rounded-xl bg-background-50 overflow-hidden hover:bg-background-200/70 transition-colors cursor-pointer"
                  >
                    <div className="h-40 overflow-hidden">
                      <img
                        src={rel.imageUrl}
                        alt={rel.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        title={`${rel.title} - 牧教师`}
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {rel.title}
                      </h3>
                      <p className="text-xs text-foreground-400 mt-2">{rel.readTime}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Back to list */}
        <section className="w-full bg-background-50 py-8">
          <div className="max-w-6xl mx-auto px-4 md:px-6 text-center">
            <Link
              to={`/category/${article.categorySlug}`}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg border border-background-300 text-foreground-600 text-sm font-medium hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回「{article.category}」
            </Link>
          </div>
        </section>
      </main>

      <Footer />

      {/* Schema.org */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://mujiaoshi.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: article.category,
                item: `https://mujiaoshi.com/category/${article.categorySlug}`,
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: article.title,
              },
            ],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            datePublished: article.date,
            image: article.imageUrl,
            url: `https://mujiaoshi.com/article/${article.slug}`,
            publisher: {
              '@type': 'Organization',
              name: '牧教师',
            },
          }),
        }}
      />
    </>
  );
}